import { useMemo, useState } from "react";
import { DIVISION_HINT, MANAGER_HINT, OWNER_HINT, TEAM_HINT } from "@/features/shared/ownerGroupLabels";
import { useNavigate } from "react-router-dom";
import type { SavedViewDto, SlownessBucket } from "@gw/shared-types";
import { useRequestListQuery } from "./useRequestListQuery";
import { useRequestAggregateQuery } from "./useRequestAggregateQuery";
import { useUrlSearchState } from "./useUrlSearchState";
import { AssistantPanel } from "@/features/assistant/AssistantPanel";
import { useSavedViews, useCreateSavedView, useDeleteSavedView } from "./useSavedViews";
import { useExportRequests } from "./useExportRequests";
import { TableSkeleton } from "@/components/Skeleton";
import { BrandedLoader } from "@/components/BrandedLoader";
import { useDelayedFlag } from "@/lib/useDelayedFlag";
import { UnitToggle } from "@/components/UnitToggle";
import { DEFAULTS, requestListSchema, type RequestListState } from "./requestListSchema";
import { slownessLabel } from "@/lib/labels";
import { SummaryTiles, type QuickFilter } from "./components/SummaryTiles";
import { InstancesTable } from "./components/InstancesTable";
import { Pagination, type PageSize } from "./components/Pagination";
import { FilterRail } from "./components/FilterRail";
import { FilterGroup } from "./components/FilterGroup";
import { DateRangeFilter } from "./components/DateRangeFilter";
import { ActiveChips } from "./components/ActiveChips";
import { SearchInput } from "./components/SearchInput";
import { SavedViewsMenu } from "./components/SavedViewsMenu";
import { SaveViewDialog } from "./components/SaveViewDialog";
import {
  RequestGroupByChips,
  REQUEST_GROUP_LABEL_BY_KEY,
} from "./components/RequestGroupByChips";
import { RequestAggregateTable } from "./components/RequestAggregateTable";
import type { SortState } from "./components/SortHead";
import type {
  AggregateRequestsParams,
  ListRequestsParams,
  RequestAggregateGroupBy,
  RequestAggregateRowDto,
} from "@/api/endpoints";

// Status filter options are stable — the SP only produces these two values, so we list them
// directly rather than rounding through the backend filter-options builder. `count: 0` is a
// placeholder; FilterGroup no longer renders counts.
const STATUS_OPTIONS = [
  { value: "Completed", count: 0 },
  { value: "InProgress", count: 0 },
];

// Fields excluded from saved-view payloads: paging shouldn't be part of the view definition.
function toSavedFilters(state: RequestListState): Record<string, unknown> {
  const { page: _p, pageSize: _ps, ...rest } = state;
  return rest;
}

function applySavedFilters(filters: Record<string, unknown>): Partial<RequestListState> {
  const parsed = requestListSchema.safeParse({ ...filters, page: 1 });
  if (!parsed.success) {
    console.warn("[savedViews] saved filters didn't parse cleanly, applying best effort", parsed.error.issues);
    const cleaned = { ...filters };
    for (const issue of parsed.error.issues) delete cleaned[issue.path[0] as string];
    return requestListSchema.parse({ ...cleaned, page: 1 });
  }
  return parsed.data;
}

function resolveDateWindow(state: RequestListState): { from: string; to: string } {
  let from = state.from;
  let to = state.to;
  if (!from || !to) {
    const now = new Date();
    from = new Date(now.getTime() - state.dateRange * 86_400_000).toISOString();
    to = now.toISOString();
  }
  return { from, to };
}

function effectiveBucketAndCalc(state: RequestListState) {
  // Quick-filter overrides preserve the user's underlying bucket/calculatedStatus filter when
  // toggled off. The "inprogress" tile pushes calculatedStatus=["InProgress"]; the underlying
  // raw `status` filter is no longer surfaced in the UI but still passes through verbatim if a
  // saved URL carries it.
  const effectiveBucket: SlownessBucket[] =
    state.quickFilter === "slow"
      ? ["Slow", "Critical"]
      : (state.bucket as SlownessBucket[]);
  const effectiveCalculatedStatus: string[] =
    state.quickFilter === "inprogress" ? ["InProgress"] : state.calculatedStatus;
  return { effectiveBucket, effectiveCalculatedStatus };
}

function toApiParams(state: RequestListState): ListRequestsParams {
  const { from, to } = resolveDateWindow(state);
  const { effectiveBucket, effectiveCalculatedStatus } = effectiveBucketAndCalc(state);
  return {
    page: state.page,
    pageSize: state.pageSize,
    from,
    to,
    sort: state.sort,
    dir: state.dir,
    unit: state.durationUnit,
    onboardingsOnly: state.onboardingsOnly,
    redeployment: state.redeployment,
    requestType: state.requestType,
    status: state.status,
    calculatedStatus: effectiveCalculatedStatus,
    client: state.client,
    lob: state.lob,
    eng: state.eng,
    assigned: state.assigned,
    team: state.team,
    division: state.division,
    manager: state.manager,
    country: state.country,
    bucket: effectiveBucket,
    rework: state.rework,
    q: state.q || undefined,
    qPills: state.qPills.length ? state.qPills : undefined,
  };
}

function toAggregateParams(state: RequestListState): AggregateRequestsParams {
  const { from, to } = resolveDateWindow(state);
  const { effectiveBucket, effectiveCalculatedStatus } = effectiveBucketAndCalc(state);
  return {
    groupBy: state.groupBy,
    from,
    to,
    minSample: state.minSample,
    unit: state.durationUnit,
    onboardingsOnly: state.onboardingsOnly,
    redeployment: state.redeployment,
    requestType: state.requestType,
    status: state.status,
    calculatedStatus: effectiveCalculatedStatus,
    client: state.client,
    lob: state.lob,
    eng: state.eng,
    assigned: state.assigned,
    team: state.team,
    division: state.division,
    manager: state.manager,
    country: state.country,
    bucket: effectiveBucket,
    rework: state.rework,
    q: state.q || undefined,
    qPills: state.qPills.length ? state.qPills : undefined,
  };
}

function applyDrilldownToInstanceParams(
  base: ListRequestsParams,
  drillKeys: RequestAggregateGroupBy[],
  drillVals: string[],
): ListRequestsParams {
  // Translate group-by keys into filter dimensions and intersect with the user's selection.
  const next = { ...base };
  for (let i = 0; i < drillKeys.length; i++) {
    const key = drillKeys[i]!;
    const val = drillVals[i]!;
    switch (key) {
      case "client":
        next.client = [val];
        break;
      case "lob":
        next.lob = [val];
        break;
      case "engagementType":
        next.eng = [val];
        break;
      case "requestType":
        next.requestType = [val];
        break;
      case "country":
        next.country = [val];
        break;
      case "assignedTo":
        next.assigned = [val];
        break;
      case "team":
        next.team = [val];
        break;
      case "division":
        next.division = [val];
        break;
      case "manager":
        next.manager = [val];
        break;
      case "calculatedStatus":
        next.calculatedStatus = [val];
        break;
    }
  }
  return next;
}

export function RequestListScreen() {
  const [state, setState] = useUrlSearchState();
  const navigate = useNavigate();
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveError, setSaveError] = useState<string | undefined>();

  const aggregateParams = useMemo(() => toAggregateParams(state), [state]);
  const instanceParams = useMemo(() => {
    const base = toApiParams(state);
    return applyDrilldownToInstanceParams(base, state.drillKeys, state.drillVals);
  }, [state]);

  const aggregateQuery = useRequestAggregateQuery(aggregateParams, state.tab === "aggregate");
  const listQuery = useRequestListQuery(instanceParams);

  const savedViewsQuery = useSavedViews("requests");
  const createSavedViewMut = useCreateSavedView("requests");
  const deleteSavedViewMut = useDeleteSavedView("requests");
  const savedViewsUnavailable =
    savedViewsQuery.isError &&
    (savedViewsQuery.error as { status?: number } | undefined)?.status === 503;

  const handleApplySavedView = (view: SavedViewDto) => {
    setState(applySavedFilters(view.filters));
  };

  const handleSaveCurrentView = async (name: string) => {
    setSaveError(undefined);
    try {
      await createSavedViewMut.mutateAsync({
        name,
        screen: "requests",
        filters: toSavedFilters(state),
      });
      setSaveOpen(false);
    } catch (err) {
      const apiErr = err as { status?: number; message?: string };
      if (apiErr.status === 409) {
        setSaveError("A view with that name already exists. Pick a different name.");
      } else {
        setSaveError(apiErr.message ?? "Could not save view.");
      }
    }
  };

  const handleDeleteSavedView = (view: SavedViewDto) => {
    if (confirm(`Delete saved view "${view.name}"?`)) {
      deleteSavedViewMut.mutate(view.id);
    }
  };

  const handleQuickFilter = (q: QuickFilter) => {
    setState({ quickFilter: q ?? undefined, page: 1 });
  };

  const handleClearAll = () => {
    setState({
      requestType: [],
      status: [],
      calculatedStatus: [],
      client: [],
      lob: [],
      eng: [],
      assigned: [],
      team: [],
      division: [],
      manager: [],
      country: [],
      bucket: [],
      rework: false,
      onboardingsOnly: false,
      redeployment: false,
      q: "",
      qPills: [],
      quickFilter: undefined,
      from: undefined,
      to: undefined,
      dateRange: DEFAULTS.dateRange,
      drillKeys: [],
      drillVals: [],
      page: 1,
    });
  };

  const handleDrillIn = (row: RequestAggregateRowDto) => {
    setState({
      tab: "instances",
      drillKeys: state.groupBy,
      drillVals: state.groupBy.map((k) => row.groupValues[k] ?? ""),
      page: 1,
    });
  };

  const handleClearDrill = () => {
    setState({ drillKeys: [], drillVals: [], page: 1 });
  };

  const instancesSummary = listQuery.data?.summary;
  const opts = instancesSummary?.filterOptions;

  const exporter = useExportRequests();
  const handleExport = () => {
    // Pass the same filter params as the list query (drill-down + quick filter resolved).
    exporter.exportNow(instanceParams);
  };

  const aggSort: SortState = { key: state.aggSort, dir: state.aggDir };
  const instanceSort: SortState = { key: state.sort, dir: state.dir };

  const handleAggSort = (s: SortState) => setState({ aggSort: s.key, aggDir: s.dir });
  const handleInstanceSort = (s: SortState) => setState({ sort: s.key, dir: s.dir, page: 1 });

  const drillLabel =
    state.drillKeys.length > 0
      ? state.drillKeys.map((k, i) => `${REQUEST_GROUP_LABEL_BY_KEY[k]}: ${state.drillVals[i]}`).join(" / ")
      : undefined;

  // Cover the content (summary + filters + table) with the branded loader while the active tab's
  // data is in flight, so partial/stale filters never show. Gated so fast warm-cache loads don't
  // flash it.
  const fetching =
    state.tab === "aggregate" ? aggregateQuery.isFetching : listQuery.isFetching;
  const showLoader = useDelayedFlag(fetching);

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Requests</h1>
          <div className="subtitle">
            {(listQuery.data?.totalCount ?? 0).toLocaleString()} items · cycle-time outliers and in-flight work.
          </div>
        </div>
        <div className="actions">
          <UnitToggle
            value={state.durationUnit}
            onChange={(u) => setState({ durationUnit: u })}
          />
          <SavedViewsMenu
            views={savedViewsQuery.data ?? []}
            onApply={handleApplySavedView}
            onSaveClick={() => {
              setSaveError(undefined);
              setSaveOpen(true);
            }}
            onDelete={handleDeleteSavedView}
            unavailable={savedViewsUnavailable}
          />
          <button
            className="btn btn-ghost btn-sm"
            onClick={handleExport}
            disabled={exporter.exporting}
            title={exporter.error ?? "Download the current filtered list as CSV"}
          >
            <span className="material-symbols-rounded">download</span>
            {exporter.exporting ? "Exporting…" : "Export"}
          </button>
        </div>
      </div>
      {exporter.error && (
        <div
          style={{
            padding: "8px 12px",
            background: "var(--gw-warning-50)",
            border: "1px solid var(--gw-warning-200, #eecf8a)",
            color: "var(--gw-warning-700)",
            fontSize: 12,
            borderRadius: 6,
            marginBottom: 12,
          }}
        >
          {exporter.error}
        </div>
      )}

      <SaveViewDialog
        open={saveOpen}
        onClose={() => setSaveOpen(false)}
        onSave={handleSaveCurrentView}
        isSubmitting={createSavedViewMut.isPending}
        errorMessage={saveError}
      />

      <div className="screen-body">
        {showLoader && <BrandedLoader />}

        {instancesSummary && (
          <SummaryTiles
            summary={instancesSummary}
            activeQuickFilter={state.quickFilter ?? null}
            onQuickFilter={handleQuickFilter}
            unit={state.durationUnit}
          />
        )}

        <div className="list-layout">
        <div className="rail-dock">
        <FilterRail onClear={handleClearAll}>
          <DateRangeFilter
            dateRange={state.dateRange}
            from={state.from}
            to={state.to}
            defaultDateRange={DEFAULTS.dateRange}
            onChange={(patch) => setState({ ...patch, page: 1 })}
          />

          {opts && (
            <>
              <FilterGroup
                label="Slowness"
                options={opts.slownessBuckets}
                selected={state.bucket}
                onChange={(arr) => setState({ bucket: arr as SlownessBucket[], page: 1 })}
                formatValue={(v) => slownessLabel(v as SlownessBucket)}
              />
              <FilterGroup
                label="Status"
                options={STATUS_OPTIONS}
                selected={state.calculatedStatus}
                onChange={(arr) =>
                  setState({
                    calculatedStatus: arr as ("Completed" | "InProgress")[],
                    page: 1,
                  })
                }
                formatValue={(v) => (v === "InProgress" ? "In Progress" : v)}
              />
              <FilterGroup
                label="Division"
                hint={DIVISION_HINT}
                options={opts.divisions ?? []}
                selected={state.division}
                onChange={(arr) => setState({ division: arr, page: 1 })}
              />
              <FilterGroup
                label="Manager"
                hint={MANAGER_HINT}
                options={opts.managers ?? []}
                selected={state.manager}
                onChange={(arr) => setState({ manager: arr, page: 1 })}
              />
              <FilterGroup
                label="Team"
                hint={TEAM_HINT}
                options={opts.teams ?? []}
                selected={state.team}
                onChange={(arr) => setState({ team: arr, page: 1 })}
                searchable
              />
              <FilterGroup
                label="Owner"
                hint={OWNER_HINT}
                options={opts.assignedUsers}
                selected={state.assigned}
                onChange={(arr) => setState({ assigned: arr, page: 1 })}
                searchable
              />
              <FilterGroup
                label="Request type"
                max={100}
                options={opts.requestTypes.filter(
                  (o) => o.value.toLowerCase() !== "onboarding",
                )}
                selected={state.requestType}
                onChange={(arr) => setState({ requestType: arr, page: 1 })}
                extraOptions={[
                  {
                    label: "Onboarding",
                    checked: state.onboardingsOnly,
                    onToggle: () =>
                      setState({ onboardingsOnly: !state.onboardingsOnly, page: 1 }),
                  },
                  {
                    label: "Redeployment",
                    checked: state.redeployment,
                    onToggle: () =>
                      setState({ redeployment: !state.redeployment, page: 1 }),
                  },
                ]}
              />
              <FilterGroup
                label="Client"
                options={opts.clients}
                selected={state.client}
                onChange={(arr) => setState({ client: arr, page: 1 })}
                searchable
              />
              <FilterGroup
                label="Line of business"
                options={opts.linesOfBusiness}
                selected={state.lob}
                onChange={(arr) => setState({ lob: arr, page: 1 })}
              />
              <FilterGroup
                label="Engagement type"
                options={opts.engagementTypes}
                selected={state.eng}
                onChange={(arr) => setState({ eng: arr, page: 1 })}
              />
              <FilterGroup
                label="Country"
                options={opts.workerCountries}
                selected={state.country}
                onChange={(arr) => setState({ country: arr, page: 1 })}
              />
            </>
          )}

          <div className="filter-group">
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={state.rework}
                onChange={() => setState({ rework: !state.rework, page: 1 })}
                style={{ accentColor: "var(--gw-primary-500)", width: 14, height: 14 }}
              />
              Has rework (rejected ≥ 1)
            </label>
          </div>
        </FilterRail>
        <AssistantPanel screen="requestList" activeFilters={state} />
        </div>

        <div className="card">
          <div className="tabs">
            <div
              className={"tab " + (state.tab === "aggregate" ? "active" : "")}
              onClick={() => setState({ tab: "aggregate", drillKeys: [], drillVals: [] })}
            >
              Aggregate
            </div>
            <div
              className={"tab " + (state.tab === "instances" ? "active" : "")}
              onClick={() => setState({ tab: "instances" })}
            >
              Instances {drillLabel ? `· filtered to ${drillLabel}` : ""}
            </div>
          </div>

          <div className="toolbar">
            <SearchInput
              value={state.q}
              onChange={(q) => setState({ q, page: 1 })}
              onCommit={(text) => {
                // Dedupe case-insensitively so re-typing the same word doesn't pile up pills.
                const existing = state.qPills.find(
                  (p) => p.toLowerCase() === text.toLowerCase(),
                );
                setState({
                  q: "",
                  qPills: existing ? state.qPills : [...state.qPills, text],
                  page: 1,
                });
              }}
            />
          </div>

          <ActiveChips
            state={state}
            defaultDateRange={DEFAULTS.dateRange}
            onClear={(patch) => setState({ ...patch, page: 1 })}
            onClearAll={handleClearAll}
          />

          {state.tab === "aggregate" && (
            <>
              <RequestGroupByChips
                selected={state.groupBy}
                onChange={(keys) => setState({ groupBy: keys })}
              />
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  fontSize: 11,
                  color: "var(--fg-3)",
                  margin: "4px 0 8px",
                }}
              >
                <span>Min sample size</span>
                <input
                  type="number"
                  min={1}
                  max={50}
                  value={state.minSample}
                  onChange={(e) => setState({ minSample: Math.max(1, Number(e.target.value) || 1) })}
                  style={{
                    width: 56,
                    padding: "2px 6px",
                    border: "1px solid var(--border-subtle)",
                    borderRadius: 4,
                    fontSize: 12,
                  }}
                />
                <span>· groups with fewer requests are hidden</span>
              </label>
              {aggregateQuery.isLoading ? (
                <TableSkeleton cols={11} rows={8} />
              ) : aggregateQuery.isError || !aggregateQuery.data ? (
                <div className="empty">
                  <h4>Could not load aggregates</h4>
                  <p>{(aggregateQuery.error as Error)?.message ?? "Unknown error"}</p>
                </div>
              ) : (
                <RequestAggregateTable
                  rows={aggregateQuery.data}
                  groupBy={state.groupBy}
                  sort={aggSort}
                  onSortChange={handleAggSort}
                  onRowClick={handleDrillIn}
                  unit={state.durationUnit}
                />
              )}
              <div className="pagination">
                <div>
                  {(aggregateQuery.data?.length ?? 0).toLocaleString()} groups ·{" "}
                  {(listQuery.data?.totalCount ?? 0).toLocaleString()} requests total
                </div>
                <div style={{ fontSize: 11, color: "var(--fg-3)" }}>
                  Click any row to drill into its instances
                </div>
              </div>
            </>
          )}

          {state.tab === "instances" && (
            <>
              {state.drillKeys.length > 0 && (
                <div className="chip-row" style={{ padding: "8px 12px", borderBottom: "1px solid var(--border-subtle)" }}>
                  <span
                    style={{
                      fontSize: 11,
                      color: "var(--fg-2)",
                      textTransform: "uppercase",
                      letterSpacing: "0.04em",
                      fontWeight: 600,
                    }}
                  >
                    Drill-down:
                  </span>
                  {state.drillKeys.map((k, i) => (
                    <span key={i} className="chip">
                      {REQUEST_GROUP_LABEL_BY_KEY[k]}: {state.drillVals[i]}
                    </span>
                  ))}
                  <span className="chip clear-all" onClick={handleClearDrill}>
                    Clear drill-down
                  </span>
                </div>
              )}

              {listQuery.isLoading ? (
                <TableSkeleton cols={11} rows={8} />
              ) : listQuery.isError || !listQuery.data ? (
                <div className="empty">
                  <h4>Could not load requests</h4>
                  <p>{(listQuery.error as Error)?.message ?? "Unknown error"}</p>
                </div>
              ) : (
                <>
                  <InstancesTable
                    rows={listQuery.data.items}
                    sort={instanceSort}
                    onSortChange={handleInstanceSort}
                    unit={state.durationUnit}
                    onRowClick={(id) => {
                      // Forward the current date window so the detail snapshot matches the one
                      // the row was found in (the default detail window is narrower than the
                      // list's, which can otherwise produce a spurious 404 — see API).
                      const { from, to } = resolveDateWindow(state);
                      navigate(
                        `/requests/${id}?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
                      );
                    }}
                  />
                  <Pagination
                    page={state.page}
                    pageSize={state.pageSize}
                    totalCount={listQuery.data.totalCount}
                    totalPages={listQuery.data.totalPages}
                    onPageChange={(p) => setState({ page: p })}
                    onPageSizeChange={(ps: PageSize) => setState({ pageSize: ps, page: 1 })}
                  />
                </>
              )}
            </>
          )}
        </div>
        </div>
      </div>
    </>
  );
}
